import {
  buildHtml,
  buildImage,
  buildLink,
} from '@amazeelabs/react-framework-bridge/gatsby';
import { useFormatDate } from '@nuklear9/i18n';
import { NewsPage, StandardLayout } from '@nuklear9/ui';
import { graphql, PageProps } from 'gatsby';
import React, { ComponentProps, PropsWithChildren } from 'react';

import {
  mapTopicListItemFragmentsToMainTopics,
  mapTopicListItemFragmentsToSubTopics,
} from '../gatsby-node/fragments/TopicListItem';
import { LocationState, PageContext } from '../types';
import { buildContent, useLayoutProps, usePrefooterPath } from '../utils';
import { drupalImageData } from '../utils/drupal-image';
import { useFooterTagCloud } from '../utils/use-footer-tagcloud';

export const mapCoverBlockFragmentToCoverProps = (
  cover: CoverBlockFragment,
): ComponentProps<typeof NewsPage>['cover'] => {
  const CoverImageLink = ({ children, ...props }: PropsWithChildren<{}>) => (
    <a
      href={encodeURI(cover?.image?.url || '#')}
      target="_blank"
      rel="noreferrer"
      {...props}
    >
      {children}
    </a>
  );
  return {
    Title: buildHtml(cover.headline || ''),
    Subtitle: cover.subtitle ? buildHtml(cover.subtitle) : undefined,
    Lead: cover.leadtext ? buildHtml(cover.leadtext) : undefined,
    figure: cover.image
      ? {
          Image: buildImage({
            image: drupalImageData(cover.image, 'content'),
            alt: cover.image?.translation?.alt || '',
          }),
          Link: CoverImageLink,
          caption: cover.imageCaption,
          source: cover.imageSource,
        }
      : undefined,
  };
};

export const NewsPageQuery = graphql`
  query NewsPage($id: String!, $locale: String!) {
    drupalSettings {
      preFooterMembership {
        translations {
          langcode
          path
        }
      }
      preFooterNewsletterSubscription {
        translations {
          langcode
          path
        }
      }
      preFooterExternalNewsletterLink {
        langcode
        path
      }
    }
    page: drupalNews(remoteId: { eq: $id }) {
      localizations: translations {
        langcode
        path
      }
      id
      ...News
    }
  }
`;

const NewsPageTemplate = ({
  data,
  pageContext,
  location,
}: PageProps<NewsPageQuery, PageContext, LocationState>) => {
  const news = data.page;
  const cover = news?.cover;

  const footerTagCloud = useFooterTagCloud(pageContext.locale);
  const externalNewsletterLinkSettings =
    data.drupalSettings?.preFooterExternalNewsletterLink?.find(
      (item) => item?.langcode === pageContext.locale,
    );
  const newsletterLinkInternalPath = usePrefooterPath(
    pageContext.locale,
    data.drupalSettings?.preFooterNewsletterSubscription?.translations,
  );
  const layout = useLayoutProps(pageContext, location, {
    seo: {
      title: news?.title,
      imageUrl: cover?.image?.url,
      imageAlt: cover?.image?.translation
        ? cover.image.translation.alt
        : undefined,
      description: cover?.leadtext,
      og: {
        type: 'article',
      },
      meta: [
        {
          property: 'article:published_time',
          content: news?.meta.articlePublishedTime || '',
        },
      ].concat(
        news?.topics.map((item) => {
          return {
            property: 'article:tag',
            content: item.title,
          };
        }) || [],
      ),
    },
    preFooter: {
      NewsletterLink: buildLink(
        externalNewsletterLinkSettings
          ? {
              href: externalNewsletterLinkSettings.path,
            }
          : {
              segments: [newsletterLinkInternalPath],
            },
      ),
      MembershipLink: buildLink({
        segments: [
          usePrefooterPath(
            pageContext.locale,
            data.drupalSettings?.preFooterMembership?.translations,
          ),
        ],
      }),
    },
    footerTags: footerTagCloud?.length
      ? footerTagCloud.map((item) => ({
          id: item?.id || '',
          Link: buildLink({ href: item?.path || '/' }),
          title: item?.title || '',
        }))
      : [],
  });

  const formatDate = useFormatDate();

  if (!news) {
    return null;
  }

  return (
    <StandardLayout {...layout} messages={location.state?.messages}>
      {cover && (
        <NewsPage
          tags={{
            tags: mapTopicListItemFragmentsToSubTopics(news?.topics),
          }}
          cover={{
            ...mapCoverBlockFragmentToCoverProps(cover),
            date: news?.publishedDate,
            topics: mapTopicListItemFragmentsToMainTopics(news?.topics),
          }}
          ReferencesProps={
            news.references && news.references?.items.length
              ? {
                  references: news.references?.items.map((reference) => ({
                    id: reference.linkUrl,
                    title: reference.linkText,
                    description: reference.description,
                    Link: buildLink({ href: reference.linkUrl }),
                  })),
                }
              : undefined
          }
          RelatedArticlesProps={
            news.relatedArticles && news.relatedArticles?.length
              ? {
                  articles: news.relatedArticles?.map((article) => ({
                    id: article!.path,
                    title: article!.title,
                    type: article!.__typename || '',
                    Link: buildLink({ href: article!.path }),
                    publishedDate: formatDate(new Date(article!.publishedDate)),
                  })),
                }
              : undefined
          }
          ContentSource={
            news.contentSource?.html
              ? buildHtml(news.contentSource.html)
              : undefined
          }
        >
          {buildContent(news.body, pageContext)}
        </NewsPage>
      )}
    </StandardLayout>
  );
};

export default NewsPageTemplate;
